import { FormEvent, useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { UserCog } from "lucide-react";
import { authAPI, DEMO_ACCOUNT_EMAIL } from "../api/authApi";

export function DemoAccountSettings() {
  const [configured, setConfigured] = useState(false);
  const [userExists, setUserExists] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [notificationEmail, setNotificationEmail] = useState("");
  const [savingNotificationEmail, setSavingNotificationEmail] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    authAPI
      .getDemoAccountStatus()
      .then((status) => {
        if (!cancelled) {
          setConfigured(status.configured);
          setUserExists(status.userExists);
          setNotificationEmail(status.notificationEmail ?? "");
          setError(null);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to load demo account settings");
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setSuccess(null);

    if (!password) {
      setError("Password is required");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setSaving(true);
    try {
      await authAPI.setDemoAccountPassword(password);
      setConfigured(true);
      setUserExists(true);
      setPassword("");
      setConfirmPassword("");
      setSuccess("Demo password updated");
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to update demo password");
    } finally {
      setSaving(false);
    }
  };

  const handleNotificationEmailSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setSuccess(null);
    setSavingNotificationEmail(true);
    try {
      await authAPI.setDemoAccountNotificationEmail(notificationEmail);
      setSuccess(
        notificationEmail.trim()
          ? "Notification email updated"
          : "Notification email cleared"
      );
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to update notification email");
    } finally {
      setSavingNotificationEmail(false);
    }
  };

  return (
    <Card className="border-violet-100/70 dark:border-violet-900/60">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <UserCog className="w-4 h-4" />
          Demo account
          <Badge variant={configured ? "default" : "secondary"} className="ml-auto">
            {loading ? "Loading..." : configured ? "Password configured" : "Not configured"}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Password login for <span className="font-medium text-foreground">{DEMO_ACCOUNT_EMAIL}</span>.
          Saving the password also creates this user if it does not exist. Set ACLs in <strong>Permissions</strong>.
          No verification email is sent for this account.
          {!loading && configured && !userExists ? (
            <span className="block mt-1">The password is saved, but the user record is missing. Save the password again to create it.</span>
          ) : null}
        </p>

        {error ? (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}

        {success ? (
          <Alert>
            <AlertDescription>{success}</AlertDescription>
          </Alert>
        ) : null}

        <form onSubmit={(event) => void handleSubmit(event)} className="space-y-3 max-w-md">
          <div className="space-y-2">
            <Label htmlFor="demo-password">New password</Label>
            <Input
              id="demo-password"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="new-password"
              disabled={saving || loading}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="demo-password-confirm">Confirm password</Label>
            <Input
              id="demo-password-confirm"
              type="password"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              autoComplete="new-password"
              disabled={saving || loading}
            />
          </div>
          <p className="text-xs text-muted-foreground">
            At least 8 characters with uppercase, lowercase, number, and special character.
          </p>
          <Button type="submit" disabled={saving || loading}>
            {saving ? "Saving..." : configured ? "Update demo password" : "Set demo password"}
          </Button>
        </form>

        <form onSubmit={(event) => void handleNotificationEmailSubmit(event)} className="space-y-3 max-w-md pt-2 border-t">
          <div className="space-y-2">
            <Label htmlFor="demo-notification-email">Login notification email</Label>
            <Input
              id="demo-notification-email"
              type="email"
              value={notificationEmail}
              onChange={(event) => setNotificationEmail(event.target.value)}
              placeholder="admin@example.com"
              autoComplete="email"
              disabled={savingNotificationEmail || loading}
            />
          </div>
          <p className="text-xs text-muted-foreground">
            When someone signs in as {DEMO_ACCOUNT_EMAIL}, an email is sent to this address with the client IP. Leave empty to disable.
          </p>
          <Button type="submit" variant="secondary" disabled={savingNotificationEmail || loading}>
            {savingNotificationEmail ? "Saving..." : "Save notification email"}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
